import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select, Textarea } from '@components';
import { BuyNumberModal } from '../../../PhoneNumbers/components';
import {
  CreateAgentData,
  VoiceOption,
  KnowledgeBaseOption,
  CallRouteOption,
  PhoneNumberOption,
  DocumentOption,
} from '../../types';
import { PurchaseNumberRequest } from '../../../PhoneNumbers/types';
import { ApiClient } from '@utils/apiClient';
import { usePhoneNumbers } from '../../../PhoneNumbers/hooks/usePhoneNumbers';
import { useToast } from '@contexts/useToast';

interface BasicInfoTabProps {
  formData: CreateAgentData;
  onInputChange: (
    field: keyof CreateAgentData,
    value: string | number | string[] | boolean
  ) => void;
  errors: Record<string, string>;
  voices: VoiceOption[];
  knowledgeBases: KnowledgeBaseOption[];
  documents: DocumentOption[];
  callRoutes: CallRouteOption[];
  phoneNumbers: PhoneNumberOption[];
  loading: boolean;
  onKnowledgeBaseChange?: (knowledgeBaseId: string) => void;
  onPhoneNumbersRefresh?: () => void;
}

const BasicInfoTab: React.FC<BasicInfoTabProps> = ({
  formData,
  onInputChange,
  errors,
  voices,
  knowledgeBases,
  documents,
  callRoutes,
  phoneNumbers,
  loading,
  onKnowledgeBaseChange,
  onPhoneNumbersRefresh,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { purchasePhoneNumber, fetchPhoneNumbers } = usePhoneNumbers();
  const [promptDescription, setPromptDescription] = useState('');
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [promptGenerationError, setPromptGenerationError] = useState<string | null>(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  const promptTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [isBuyModalOpen, setIsBuyModalOpen] = useState(false);
  const [isSnippetDropdownOpen, setIsSnippetDropdownOpen] = useState(false);
  const snippetDropdownRef = useRef<HTMLDivElement>(null);
  const savedScrollPosition = useRef<number | null>(null);
  const savedCursorPosition = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Extract variable names from variable_extraction schema
  const extractedVariables = useMemo(() => {
    if (!formData.variable_extraction) return [];

    try {
      const parsed = JSON.parse(formData.variable_extraction);
      if (parsed.schema?.properties) {
        return Object.keys(parsed.schema.properties);
      }
    } catch (e) {
      // Invalid JSON, return empty array
    }
    return [];
  }, [formData.variable_extraction]);

  const isBotEnabled =
    formData.name &&
    formData.voice &&
    formData.phone_number &&
    formData.intro_message &&
    formData.prompt;

  // Get current voice audio URL
  const selectedVoice = voices.find(v => v.id === formData.voice);
  const currentVoiceAudioUrl = selectedVoice?.demo_audio_url || null;

  // Reset audio when voice changes
  useEffect(() => {
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.load(); // Force reload of new source
    }
  }, [currentVoiceAudioUrl]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        snippetDropdownRef.current &&
        !snippetDropdownRef.current.contains(event.target as Node)
      ) {
        setIsSnippetDropdownOpen(false);
      }
    };

    if (isSnippetDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSnippetDropdownOpen]);

  // Restore scroll and cursor position after re-render
  useEffect(() => {
    const textarea = promptTextareaRef.current;
    if (textarea && savedScrollPosition.current !== null) {
      textarea.scrollTop = savedScrollPosition.current;
      if (savedCursorPosition.current !== null) {
        textarea.setSelectionRange(savedCursorPosition.current, savedCursorPosition.current);
      }
      savedScrollPosition.current = null;
      savedCursorPosition.current = null;
    }
  }, [forceUpdate]);

  const insertSnippetAtCursor = (snippet: string) => {
    const textarea = promptTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = textarea.value;

    // Add newlines around the snippet for better formatting
    const prefix = start > 0 && currentValue[start - 1] !== '\n' ? '\n' : '';
    const suffix = end < currentValue.length && currentValue[end] !== '\n' ? '\n' : '';
    const snippetWithFormatting = prefix + snippet + suffix;

    const newValue =
      currentValue.substring(0, start) + snippetWithFormatting + currentValue.substring(end);

    // Calculate new cursor position
    const newCursorPos = start + snippetWithFormatting.length;

    // Save scroll and cursor positions before React re-render
    savedScrollPosition.current = textarea.scrollTop;
    savedCursorPosition.current = newCursorPos;

    // Update the state (triggers re-render)
    onInputChange('prompt', newValue);
    setForceUpdate(prev => prev + 1);
  };

  const handleGeneratePrompt = async () => {
    if (!promptDescription.trim()) {
      setPromptGenerationError('Please enter a description for your agent');
      return;
    }

    setIsGeneratingPrompt(true);
    setPromptGenerationError(null);

    try {
      const response = await ApiClient.post('/agents/openai/generate-prompt/', {
        prompt: promptDescription,
        context: {
          agent_name: formData.name || '',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.optimized_prompt) {
          const promptContent = data.optimized_prompt.replace(/^"|"$/g, '');

          if (promptTextareaRef.current) {
            promptTextareaRef.current.value = promptContent;
            const event = new Event('input', { bubbles: true });
            promptTextareaRef.current.dispatchEvent(event);
          }

          onInputChange('prompt', promptContent);
          setForceUpdate(prev => prev + 1);
          setPromptDescription('');
        } else {
          setPromptGenerationError('No prompt generated. Please try again.');
        }
      } else {
        const errorData = await response.json();
        setPromptGenerationError(
          errorData.message || 'Failed to generate prompt. Please try again.'
        );
      }
    } catch (error) {
      setPromptGenerationError('An error occurred while generating the prompt. Please try again.');
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  const handlePurchaseNumber = async (data: PurchaseNumberRequest) => {
    const result = await purchasePhoneNumber(data);

    if (result.success && result.phoneNumber) {
      showToast({
        type: 'success',
        title: 'Phone Number Purchased',
        message: `Successfully purchased phone number: ${result.phoneNumber.number}`,
      });

      // Refresh phone numbers in both hooks
      await fetchPhoneNumbers();
      if (onPhoneNumbersRefresh) {
        await onPhoneNumbersRefresh();
      }

      setIsBuyModalOpen(false);

      // Automatically select the newly purchased number
      onInputChange('phone_number', result.phoneNumber.id);
    } else {
      // Show error toast with the error message (handles 400 errors and other errors)
      showToast({
        type: 'error',
        title: 'Purchase Failed',
        message: result.error || 'Failed to purchase phone number',
      });
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('agents.basicInformation')}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {t('agents.configureBasicDetails')}
        </p>
      </div>

      {/* Agent Details Section */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">person</span>
          <h4 className="text-md font-semibold text-gray-900 dark:text-white">
            {t('agents.agentDetails')}
          </h4>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="agent-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('agents.agentNameRequired')}
            </label>
            <Input
              id="agent-name"
              type="text"
              value={formData.name}
              onChange={e => onInputChange('name', e.target.value)}
              placeholder={t('agents.enterAgentName')}
              required
              state={errors.name ? 'error' : 'default'}
              errorText={errors.name}
              className="w-full"
            />
          </div>

          <div>
            <label
              htmlFor="voice-model"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('agents.voiceRequired')}
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Select
                  id="voice"
                  value={formData.voice || ''}
                  onChange={value => {
                    const selectedValue = Array.isArray(value) ? value[0] : value;
                    onInputChange('voice', selectedValue || '');
                  }}
                  placeholder={t('agents.selectVoice')}
                  options={[
                    { value: '', label: t('agents.selectVoice') },
                    ...voices.map(voice => ({
                      value: voice.id,
                      label: `${voice.name} (${voice.voice_type_display})`,
                    })),
                  ]}
                  error={errors.voice ? true : undefined}
                  errorText={errors.voice}
                  className="w-full"
                  disabled={loading}
                />
              </div>
              {currentVoiceAudioUrl && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      if (audioRef.current) {
                        if (isPlaying) {
                          audioRef.current.pause();
                          audioRef.current.currentTime = 0;
                          setIsPlaying(false);
                        } else {
                          // Ensure we have the latest source loaded
                          audioRef.current.load();
                          audioRef.current.play();
                          setIsPlaying(true);
                        }
                      }
                    }}
                    className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/40 dark:hover:bg-blue-900/60 text-blue-600 dark:text-blue-400 transition-colors flex items-center justify-center"
                    title={t('agents.playVoiceDemo')}
                  >
                    <span className="material-icons text-xl">
                      {isPlaying ? 'stop' : 'play_arrow'}
                    </span>
                  </button>
                  <audio
                    key={currentVoiceAudioUrl}
                    ref={audioRef}
                    onEnded={() => setIsPlaying(false)}
                    onPause={() => setIsPlaying(false)}
                    src={currentVoiceAudioUrl}
                  >
                    <track kind="captions" />
                  </audio>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Knowledge & Documents Section */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
            library_books
          </span>
          <h4 className="text-md font-semibold text-gray-900 dark:text-white">
            {t('agents.knowledgeAndDocuments')}
          </h4>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="knowledge-base"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('agents.knowledgeBase')}
            </label>
            <Select
              id="knowledge-base"
              value={formData.knowledge_base || ''}
              onChange={value => {
                const kbId = Array.isArray(value) ? value[0] : value;
                onInputChange('knowledge_base', kbId || '');
                onInputChange('documents', []);
                if (onKnowledgeBaseChange && kbId) {
                  onKnowledgeBaseChange(kbId);
                }
              }}
              placeholder={t('agents.selectKnowledgeBase')}
              options={[
                { value: '', label: t('agents.selectKnowledgeBase') },
                ...knowledgeBases.map(kb => ({
                  value: kb.id,
                  label: kb.name,
                })),
              ]}
              className="w-full"
              disabled={loading}
              error={errors.knowledge_base ? true : undefined}
              errorText={errors.knowledge_base}
            />
          </div>

          <div>
            <label
              htmlFor="document"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('agents.documents')}
            </label>
            <Select
              id="documents"
              value={formData.documents}
              onChange={value => onInputChange('documents', Array.isArray(value) ? value : [value])}
              placeholder={t('agents.selectOption')}
              options={[
                ...documents.map(doc => {
                  const title = doc.title?.trim();
                  const fileName = doc.file
                    ? doc.file.split('/').pop() || doc.file.split('\\').pop() || doc.file
                    : null;
                  const displayName = title || fileName || `Document ${doc.id.slice(0, 8)}`;
                  return {
                    value: doc.id,
                    label: displayName,
                  };
                }),
              ]}
              className="w-full"
              disabled={!formData.knowledge_base || loading}
              multiple
              error={errors.documents ? true : undefined}
              errorText={errors.documents}
            />
          </div>
        </div>
      </div>

      {/* Communication Section */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">phone</span>
          <h4 className="text-md font-semibold text-gray-900 dark:text-white">
            {t('agents.communication')}
          </h4>
        </div>

        <div className="space-y-6">
          {/* Call Routes */}
          <div>
            <label
              htmlFor="call-routes"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('agents.callRoutes')}
            </label>
            <Select
              id="call-routes"
              value={formData.call_routes}
              onChange={value =>
                onInputChange('call_routes', Array.isArray(value) ? value : [value])
              }
              placeholder={t('agents.selectOption')}
              options={[
                ...callRoutes.map(route => ({
                  value: route.id,
                  label: `${route.name} (${route.phone_number})`,
                })),
              ]}
              className="w-full"
              disabled={loading}
              multiple
              error={errors.call_routes ? true : undefined}
              errorText={errors.call_routes}
            />
          </div>
        </div>
      </div>

      {/* System Prompt Section */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
            smart_toy
          </span>
          <h4 className="text-md font-semibold text-gray-900 dark:text-white">
            {t('agents.systemPrompt')}
          </h4>
        </div>

        <div className="space-y-6">
          {/* Intro Message */}
          <div>
            <label
              htmlFor="intro-message"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('agents.introMessageRequired')}
            </label>
            <Input
              id="intro-message"
              type="text"
              value={formData.intro_message}
              onChange={e => onInputChange('intro_message', e.target.value)}
              placeholder={t('agents.defaultIntroMessage')}
              required
              state={errors.intro_message ? 'error' : 'default'}
              errorText={errors.intro_message}
              className="w-full"
            />

            {/* Recording Notice */}
            <div className="mt-4 flex items-center">
              <input
                id="recording-notice-enabled"
                type="checkbox"
                checked={formData.recording_notice_enabled ?? true}
                onChange={e => onInputChange('recording_notice_enabled', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label
                htmlFor="recording-notice-enabled"
                className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
              >
                {t('agents.recordingNoticeEnabled')}
              </label>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
              {t('agents.recordingNoticeHelp')}
            </p>
          </div>

          {/* System Prompt */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label
                htmlFor="system-prompt"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t('agents.promptRequired')}
              </label>

              {/* Snippet Insertion Button */}
              {(formData.call_routes.length > 0 ||
                formData.documents.length > 0 ||
                extractedVariables.length > 0) && (
                <div className="relative" ref={snippetDropdownRef}>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => setIsSnippetDropdownOpen(!isSnippetDropdownOpen)}
                  >
                    <span className="material-icons text-sm mr-1">add</span>
                    {t('agents.insertSnippet')}
                  </button>

                  {/* Dropdown Menu */}
                  {isSnippetDropdownOpen && (
                    <div
                      id="snippet-dropdown"
                      className="absolute right-0 z-10 w-80 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-y-auto"
                    >
                      {/* Call Routes Section */}
                      {formData.call_routes.length > 0 && (
                        <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                            {t('agents.callRoutes')}
                          </div>
                          {callRoutes
                            .filter(route => formData.call_routes.includes(route.id))
                            .map(route => (
                              <button
                                key={route.id}
                                type="button"
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                onClick={() => {
                                  const snippet = t('agents.snippetCallRoute', {
                                    routeName: route.name,
                                    phoneNumber: route.phone_number,
                                  });
                                  insertSnippetAtCursor(snippet);
                                  setIsSnippetDropdownOpen(false);
                                }}
                              >
                                <div className="flex items-start">
                                  <span className="material-icons text-sm text-primary-500 dark:text-primary-300 mr-2 mt-0.5">
                                    phone_forwarded
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                      {route.name}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                      {route.phone_number}
                                    </div>
                                  </div>
                                </div>
                              </button>
                            ))}
                        </div>
                      )}

                      {/* Documents Section */}
                      {formData.documents.length > 0 && (
                        <div className="p-2">
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                            {t('agents.documents')}
                          </div>
                          {documents
                            .filter(doc => formData.documents.includes(doc.id))
                            .map(doc => {
                              const title = doc.title?.trim();
                              const fileName = doc.file
                                ? doc.file.split('/').pop() ||
                                  doc.file.split('\\').pop() ||
                                  doc.file
                                : null;
                              const displayName =
                                title || fileName || `Document ${doc.id.slice(0, 8)}`;
                              return (
                                <button
                                  key={doc.id}
                                  type="button"
                                  className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                  onClick={() => {
                                    const snippet = t('agents.snippetDocument', {
                                      documentName: displayName,
                                    });
                                    insertSnippetAtCursor(snippet);
                                    setIsSnippetDropdownOpen(false);
                                  }}
                                >
                                  <div className="flex items-start">
                                    <span className="material-icons text-sm text-primary-500 dark:text-primary-300 mr-2 mt-0.5">
                                      description
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {displayName}
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                        </div>
                      )}

                      {/* Variables Section */}
                      {extractedVariables.length > 0 && (
                        <div
                          className={`p-2 ${formData.call_routes.length > 0 || formData.documents.length > 0 ? 'border-t border-gray-200 dark:border-gray-700' : ''}`}
                        >
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                            {t('agents.extractedVariables')}
                          </div>
                          {extractedVariables.map(variable => (
                            <button
                              key={variable}
                              type="button"
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                              onClick={() => {
                                insertSnippetAtCursor(`{{ ${variable} }}`);
                                setIsSnippetDropdownOpen(false);
                              }}
                            >
                              <div className="flex items-start">
                                <span className="material-icons text-sm text-blue-500 dark:text-blue-400 mr-2 mt-0.5">
                                  data_object
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {variable}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                    {`{{ ${variable} }}`}
                                  </div>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <Textarea
              ref={promptTextareaRef}
              id="system-prompt"
              className="w-full"
              rows={15}
              value={formData.prompt}
              onChange={e => onInputChange('prompt', e.target.value)}
              placeholder={t('agents.enterSystemPrompt')}
              required
              state={errors.prompt ? 'error' : 'default'}
              errorText={errors.prompt}
              key={forceUpdate}
            />

            {/* AI Prompt Generator */}
            <div className="mt-3 space-y-3">
              <Input
                type="text"
                placeholder={t('agents.describeAgentPurpose')}
                className="w-full"
                value={promptDescription}
                onChange={e => {
                  setPromptDescription(e.target.value);
                  setPromptGenerationError(null);
                }}
                state={promptGenerationError ? 'error' : 'default'}
                errorText={promptGenerationError || undefined}
                disabled={isGeneratingPrompt}
              />
              <Button
                variant="gradient-multi"
                className="w-full"
                onClick={handleGeneratePrompt}
                disabled={isGeneratingPrompt || !promptDescription.trim()}
              >
                {isGeneratingPrompt ? (
                  <>
                    <span className="material-icons text-sm mr-1 animate-spin">refresh</span>
                    {t('agents.creating')}
                  </>
                ) : (
                  <>
                    <span className="material-icons text-sm mr-1">auto_awesome</span>
                    {t('agents.generateWithAI')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Phone & Status Section */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
            settings
          </span>
          <h4 className="text-md font-semibold text-gray-900 dark:text-white">
            {t('agents.phoneAndStatus')}
          </h4>
        </div>

        <div className="space-y-6">
          {/* Phone Number */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3">
              <label
                htmlFor="phone-number"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('agents.phoneNumber')}
              </label>
              <Select
                id="phone-number"
                value={formData.phone_number ? String(formData.phone_number) : ''}
                onChange={value => {
                  const selectedValue = Array.isArray(value) ? value[0] : value;
                  onInputChange(
                    'phone_number',
                    selectedValue && selectedValue !== '' ? selectedValue : ''
                  );
                }}
                placeholder={t('agents.selectPhoneNumber')}
                options={[
                  { value: '', label: t('agents.selectPhoneNumber') },
                  ...phoneNumbers.map(phone => ({
                    value: phone.id,
                    label: phone.number,
                  })),
                ]}
                className="w-full"
                disabled={loading}
                error={errors.phone_number ? true : undefined}
                errorText={errors.phone_number}
              />
            </div>

            <div className="flex items-end">
              <Button
                variant="gradient-multi"
                className="w-full"
                onClick={() => setIsBuyModalOpen(true)}
              >
                <span className="material-icons text-sm mr-1">phone</span>
                {t('agents.buyNumber')}
              </Button>
            </div>
          </div>

          {/* Bot Status */}
          <div>
            <label
              htmlFor="bot-status"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('agents.botStatus')}
            </label>
            <div className="flex items-center space-x-3">
              <label
                htmlFor="bot-status-toggle"
                className="relative inline-flex items-center cursor-pointer"
              >
                <span className="sr-only">{t('agents.toggleBotStatus')}</span>
                <input
                  id="bot-status-toggle"
                  type="checkbox"
                  className="sr-only peer"
                  disabled={!isBotEnabled}
                  checked={formData.status === 'active'}
                  onChange={e => onInputChange('status', e.target.checked ? 'active' : 'inactive')}
                />
                <div
                  className={`w-11 h-6 rounded-full peer transition-colors ${
                    !isBotEnabled
                      ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                      : 'bg-gray-200 dark:bg-gray-700 peer-checked:bg-primary-600'
                  } peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600`}
                ></div>
              </label>
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {formData.status === 'active' ? t('agents.turnBotOff') : t('agents.turnBotOn')}
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('agents.completeRequiredFields')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Buy Number Modal */}
      <BuyNumberModal
        isOpen={isBuyModalOpen}
        onClose={() => setIsBuyModalOpen(false)}
        onPurchase={handlePurchaseNumber}
      />
    </div>
  );
};

export { BasicInfoTab };
